import React, { useEffect } from 'react';
import { View, TouchableOpacity, StyleSheet, ViewStyle, StyleProp } from 'react-native';
import Animated from 'react-native-reanimated';
import { useAccordionAnimation } from '@/hooks/common/useAccordionAnimation';

interface AnimatedAccordionProps {
  isExpanded: boolean;
  onToggle: () => void;
  header: React.ReactNode;
  chevron: React.ReactNode;
  children: React.ReactNode;
  containerStyle?: StyleProp<ViewStyle>;
  headerStyle?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
  accessibilityHint?: string;
}

export const AnimatedAccordion: React.FC<AnimatedAccordionProps> = ({
  isExpanded,
  onToggle,
  header,
  chevron,
  children,
  containerStyle,
  headerStyle,
  contentStyle,
  accessibilityLabel,
  accessibilityHint,
}) => {
  const { setOpen, animatedHeightStyle, animatedChevronStyle, onContentLayout } =
    useAccordionAnimation({ initiallyOpen: isExpanded });

  useEffect(() => {
    setOpen(isExpanded);
  }, [isExpanded, setOpen]);

  return (
    <View style={containerStyle}>
      <TouchableOpacity
        style={headerStyle}
        onPress={onToggle}
        activeOpacity={0.7}
        accessibilityLabel={accessibilityLabel}
        accessibilityHint={accessibilityHint}
        accessibilityRole="button"
        accessibilityState={{ expanded: isExpanded }}
      >
        {header}
        <Animated.View style={animatedChevronStyle}>
          {chevron}
        </Animated.View>
      </TouchableOpacity>
      <Animated.View style={animatedHeightStyle}>
        <View onLayout={onContentLayout} style={styles.measureWrapper}>
          <View style={contentStyle}>
            {children}
          </View>
        </View>
      </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  measureWrapper: {
    position: 'absolute',
    width: '100%',
  },
});
